import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download, Film, PanelLeftClose, PanelLeftOpen, Redo2, Undo2 } from 'lucide-react';
import clsx from 'clsx';
import { CanvasHost } from './ui/CanvasHost';
import { TextPanel } from './ui/panels/TextPanel';
import { CanvasPanel } from './ui/panels/CanvasPanel';
import { LayerPanel } from './ui/panels/LayerPanel';
import { PostFxPanel } from './ui/panels/PostFxPanel';
import { PresetMenu } from './ui/panels/PresetMenu';
import { ExportDialog } from './ui/panels/ExportDialog';
import { AnimateDialog } from './ui/panels/AnimateDialog';
import { ShortcutsDialog } from './ui/panels/ShortcutsDialog';
import { documentFromLocation } from './core/export/preset';
import { ParamsPanel } from './ui/panels/ParamsPanel';
import { BatchStudio } from './ui/batch/BatchStudio';
import { EvolveStudio } from './ui/evolve/EvolveStudio';
import { useFontLoader } from './ui/useFontLoader';
import { useTextField } from './ui/useTextField';
import { useStudio } from './store/studioStore';
import { STRENGTH_LABELS, type RandomizeStrength } from './core/params/randomize';
import { clamp } from './core/math';
import './techniques/register';

const MIN_PANEL = 220;
const MAX_PANEL = 460;

type Mode = 'studio' | 'batch' | 'evolve';

const MODE_LABELS: Record<Mode, string> = {
  studio: 'Studio',
  batch: 'Batch Studio',
  evolve: 'Evolution',
};

export function App(): JSX.Element {
  useFontLoader();
  const result = useTextField();

  // The studio stays mounted while the batch tab is open, so switching back is instant
  // and neither the text field nor the layer simulations have to be rebuilt.
  const [mode, setMode] = useState<Mode>('studio');
  const [leftWidth, setLeftWidth] = useState(292);
  const [rightWidth, setRightWidth] = useState(292);
  const [panelsOpen, setPanelsOpen] = useState(true);
  const [randomizeOpen, setRandomizeOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [animateOpen, setAnimateOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const undoAction = useStudio((s) => s.undoAction);
  const redoAction = useStudio((s) => s.redoAction);
  const randomizeAll = useStudio((s) => s.randomizeAll);
  const canUndo = useStudio((s) => s.history.past.length > 0);
  const canRedo = useStudio((s) => s.history.future.length > 0);
  const lastAction = useStudio((s) => s.lastAction);
  const replaceDocument = useStudio((s) => s.replaceDocument);

  // A share link carries the whole composition in the URL fragment. Load it once on
  // mount, then clear the fragment so later edits are not confused with the shared
  // state and a reload does not silently undo the user's work.
  useEffect(() => {
    const shared = documentFromLocation(window.location.hash);
    if (!shared) return;
    replaceDocument(shared.doc, 'Geteilte Komposition geladen');
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }, [replaceDocument]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target;
      const typing =
        t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      const mod = e.metaKey || e.ctrlKey;

      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redoAction();
        else undoAction();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redoAction();
        return;
      }
      if (mod && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        if (e.shiftKey) setAnimateOpen(true);
        else setExportOpen(true);
        return;
      }
      if (typing) return;
      if (e.key === 'Tab') {
        e.preventDefault();
        setPanelsOpen((v) => !v);
      }
      if (e.key === '?') {
        e.preventDefault();
        setShortcutsOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undoAction, redoAction]);

  return (
    <div className="flex h-full flex-col bg-ink-950">
      <header className="relative z-30 flex h-11 shrink-0 items-center gap-2 border-b border-ink-800 bg-ink-900 px-3">
        <button
          type="button"
          onClick={() => setPanelsOpen((v) => !v)}
          className="ghost-button h-7 w-7 p-0"
          title="Panels ein-/ausblenden (Tab)"
        >
          {panelsOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
        </button>

        <span className="text-[13px] font-semibold tracking-tight text-ink-100">Typo Studio</span>

        <div className="ml-1 flex gap-0.5 rounded-md bg-ink-850 p-0.5">
          {(['studio', 'batch', 'evolve'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={clsx(
                'rounded px-2.5 py-1 text-[11px] transition-colors',
                mode === m ? 'bg-ink-700 text-ink-100' : 'text-ink-400 hover:text-ink-200',
              )}
            >
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>

        <div className="ml-2 flex items-center gap-0.5">
          <button
            type="button"
            className="ghost-button h-7 w-7 p-0"
            title="Rückgängig (Ctrl+Z)"
            onClick={undoAction}
            disabled={!canUndo}
          >
            <Undo2 size={13} />
          </button>
          <button
            type="button"
            className="ghost-button h-7 w-7 p-0"
            title="Wiederholen (Ctrl+Shift+Z)"
            onClick={redoAction}
            disabled={!canRedo}
          >
            <Redo2 size={13} />
          </button>
        </div>

        <RandomizeMenu
          open={randomizeOpen}
          setOpen={setRandomizeOpen}
          onPick={(strength) => {
            randomizeAll(strength);
            setRandomizeOpen(false);
          }}
        />

        <PresetMenu />

        <button
          type="button"
          onClick={() => setExportOpen(true)}
          className="ghost-button gap-1.5 px-2 py-1 text-[11px]"
          title="Exportieren (Ctrl+E)"
        >
          <Download size={12} />
          Export
        </button>

        <button
          type="button"
          onClick={() => setAnimateOpen(true)}
          className="ghost-button gap-1.5 px-2 py-1 text-[11px]"
          title="Als GIF, Video oder Bildfolge exportieren (Ctrl+Shift+E)"
        >
          <Film size={12} />
          Animation
        </button>

        {lastAction && <span className="ml-2 truncate font-mono text-[10px] text-ink-600">{lastAction}</span>}
      </header>

      {exportOpen && <ExportDialog onClose={() => setExportOpen(false)} />}
      {animateOpen && <AnimateDialog onClose={() => setAnimateOpen(false)} />}
      {shortcutsOpen && <ShortcutsDialog onClose={() => setShortcutsOpen(false)} />}

      {mode === 'batch' && <BatchStudio />}
      {mode === 'evolve' && <EvolveStudio />}

      <div className={clsx('flex min-h-0 flex-1', mode !== 'studio' && 'hidden')}>
        {panelsOpen && (
          <>
            <aside className="shrink-0 overflow-y-auto border-r border-ink-800 bg-ink-900" style={{ width: leftWidth }}>
              <TextPanel />
              <div className="border-t border-ink-800">
                <CanvasPanel />
              </div>
              <div className="border-t border-ink-800">
                <LayerPanel />
              </div>
              <div className="border-t border-ink-800">
                <PostFxPanel />
              </div>
            </aside>
            <Resizer onResize={(dx, start) => setLeftWidth(clamp(start + dx, MIN_PANEL, MAX_PANEL))} width={leftWidth} />
          </>
        )}

        <CanvasHost result={result} />

        {panelsOpen && (
          <>
            <Resizer
              onResize={(dx, start) => setRightWidth(clamp(start - dx, MIN_PANEL, MAX_PANEL))}
              width={rightWidth}
            />
            <aside className="shrink-0 border-l border-ink-800 bg-ink-900" style={{ width: rightWidth }}>
              <ParamsPanel />
            </aside>
          </>
        )}
      </div>
    </div>
  );
}

function Resizer({ onResize, width }: { onResize: (dx: number, startWidth: number) => void; width: number }): JSX.Element {
  const drag = useRef<{ x: number; width: number } | null>(null);
  return (
    <div
      className="w-[3px] shrink-0 cursor-col-resize bg-ink-800 transition-colors hover:bg-ink-600"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { x: e.clientX, width };
      }}
      onPointerMove={(e) => {
        if (drag.current) onResize(e.clientX - drag.current.x, drag.current.width);
      }}
      onPointerUp={() => {
        drag.current = null;
      }}
    />
  );
}

function RandomizeMenu({
  open,
  setOpen,
  onPick,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  onPick: (strength: RandomizeStrength) => void;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, setOpen]);

  const hints: Record<RandomizeStrength, string> = {
    gentle: '±10 % — die Nachbarschaft des aktuellen Bildes',
    medium: '±40 % — deutlich andere Varianten',
    wild: 'Voller Bereich — alles neu würfeln',
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="ghost-button gap-1 px-2 py-1 text-[11px]"
        title="Alle nicht gesperrten Parameter neu würfeln"
      >
        Randomize
        <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute left-0 top-8 z-50 w-[248px] rounded-lg border border-ink-700 bg-ink-850 py-1 shadow-2xl shadow-black/60">
          {(Object.keys(hints) as RandomizeStrength[]).map((strength) => (
            <button
              key={strength}
              type="button"
              onClick={() => onPick(strength)}
              className={clsx('block w-full px-3 py-1.5 text-left transition-colors hover:bg-ink-800')}
            >
              <div className="text-[12px] text-ink-100">{STRENGTH_LABELS[strength]}</div>
              <div className="text-[10px] text-ink-500">{hints[strength]}</div>
            </button>
          ))}
          <p className="border-t border-ink-750 px-3 py-1.5 text-[10px] leading-relaxed text-ink-600">
            Gesperrte Parameter bleiben unverändert. Der Explorationsbereich pro Parameter begrenzt, was gewürfelt
            werden darf.
          </p>
        </div>
      )}
    </div>
  );
}
